import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { CreditCard, CheckCircle, WarningCircle, Receipt, SpinnerGap, Bank } from "@phosphor-icons/react";
import { fmtDate } from "../lib/dateUtil";

/**
 * Public invoice payment page — /pay/invoice/:token
 * No login required. Amount is determined server-side from the invoice.
 */
export default function PayInvoicePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [payState, setPayState] = useState(sessionId ? "checking" : "idle"); // idle | checking | paid | expired | failed
  const pollRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get(`/pay/invoice/${token}`);
      setInvoice(data);
      if (data.status === "paid") setPayState("paid");
    } catch (e) {
      setError(e.response?.status === 404 ? "This payment link is invalid or has been removed." : "Could not load invoice. Please try again.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll checkout status when returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollRef.current += 1;
      try {
        const { data } = await API.get(`/pay/invoice/${token}/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid" || data.invoice_status === "paid") {
          setPayState("paid");
          load();
          return;
        }
        if (data.session_status === "expired") { setPayState("expired"); return; }
      } catch {
        /* keep polling */
      }
      if (pollRef.current >= 8) { setPayState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, token, load]);

  const startPayment = async () => {
    setPaying(true);
    try {
      const { data } = await API.post(`/pay/invoice/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start payment — please try again.");
      setPaying(false);
    }
  };

  const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const accent = invoice?.primary_color || "#1D4ED8";
  const logo = invoice?.logo_url ? (invoice.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${invoice.logo_url}` : invoice.logo_url) : "";

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="pay-invoice-page" style={{ background: `radial-gradient(1200px 600px at 50% -10%, ${accent}33, #020617 60%)` }}>
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800">
          <div className="px-6 py-5 text-white" style={{ background: accent }} data-testid="pay-header">
            <div className="flex items-center gap-3">
              {logo ? (
                <img src={logo} alt="" className="h-10 max-w-[140px] w-auto rounded-md object-contain bg-white px-1.5 py-1" data-testid="pay-company-logo" />
              ) : (
                <Receipt size={26} className="text-white/90" />
              )}
              <div>
                <p className="text-sm font-semibold" data-testid="pay-company-name">{invoice?.company_name || "Invoice Payment"}</p>
                <p className="text-[11px] text-white/75">{invoice?.tagline || "Secure invoice payment"}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {error ? (
              <div className="text-center py-8" data-testid="pay-error">
                <WarningCircle size={36} className="mx-auto text-red-500 mb-2" />
                <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
              </div>
            ) : !invoice ? (
              <div className="text-center py-10">
                <SpinnerGap size={28} className="mx-auto animate-spin text-slate-400" />
              </div>
            ) : payState === "paid" ? (
              <div className="text-center py-6" data-testid="pay-success">
                <CheckCircle size={44} weight="fill" className="mx-auto text-emerald-500 mb-3" />
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Payment received</p>
                <p className="text-sm text-slate-500 mt-1">
                  Invoice <span className="font-mono font-medium">{invoice.reference}</span> — {fmt(invoice.amount)}
                </p>
                <p className="text-xs text-slate-400 mt-3">A receipt has been recorded. Thank you!</p>
              </div>
            ) : payState === "checking" ? (
              <div className="text-center py-8" data-testid="pay-checking">
                <SpinnerGap size={32} className="mx-auto animate-spin text-emerald-500 mb-3" />
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Confirming your payment…</p>
                <p className="text-xs text-slate-400 mt-1">This usually takes a couple of seconds.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3" data-testid="pay-summary">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Invoice</span>
                    <span className="font-mono font-semibold text-slate-900 dark:text-white">{invoice.reference}</span>
                  </div>
                  {invoice.title && (
                    <div className="flex justify-between text-sm gap-4">
                      <span className="text-slate-500 shrink-0">For</span>
                      <span className="text-right text-slate-700 dark:text-slate-200">{invoice.title}</span>
                    </div>
                  )}
                  {invoice.due_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Due date</span>
                      <span className="text-slate-700 dark:text-slate-200">{fmtDate(invoice.due_date)}</span>
                    </div>
                  )}
                  {invoice.vat_enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Includes VAT</span>
                      <span className="text-slate-700 dark:text-slate-200">{fmt(invoice.vat_amount)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex justify-between items-baseline">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Amount due</span>
                    <span className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="pay-amount">{fmt(invoice.amount)}</span>
                  </div>
                </div>

                {(payState === "expired" || payState === "failed") && (
                  <p className="text-xs text-amber-600 mt-3 text-center" data-testid="pay-retry-note">
                    {payState === "expired" ? "Your previous payment session expired — please try again." : "We couldn't confirm the payment yet. If you completed payment, it will reflect shortly; otherwise try again."}
                  </p>
                )}

                {invoice.card_payments_enabled ? (
                  <>
                    <Button
                      className="w-full mt-5 h-12 text-base text-white hover:opacity-90"
                      style={{ background: accent }}
                      onClick={startPayment}
                      disabled={paying}
                      data-testid="pay-now-btn"
                    >
                      <CreditCard size={18} className="mr-2" />
                      {paying ? "Redirecting…" : `Pay ${fmt(invoice.amount)} by Card`}
                    </Button>
                    <p className="text-[10px] text-slate-400 text-center mt-3">Payments are processed securely by Stripe. {invoice.company_name || "We"} never see{invoice.company_name ? "s" : ""} your card details.</p>
                  </>
                ) : (
                  <div className="mt-5 rounded-lg border border-slate-200 dark:border-slate-700 p-4" data-testid="pay-bank-transfer">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Bank size={16} /> Pay by bank transfer</p>
                    <p className="text-[11px] text-slate-500 mt-1">{invoice.company_name || "This business"} doesn't take card payments online. Please transfer the amount due to:</p>
                    <dl className="mt-3 space-y-1.5 text-sm">
                      {[["Account name", invoice.bank?.account_name], ["Bank", invoice.bank?.name], ["Sort code", invoice.bank?.sort_code], ["Account number", invoice.bank?.account_number],
                        ["Reference", invoice.bank?.reference_label || invoice.reference]].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-4">
                          <dt className="text-slate-500">{k}</dt>
                          <dd className="font-mono font-medium text-slate-800 dark:text-slate-100 text-right">{v}</dd>
                        </div>
                      ))}
                    </dl>
                    {invoice.bank?.payment_instructions && <p className="text-[11px] text-slate-500 mt-3 whitespace-pre-line">{invoice.bank.payment_instructions}</p>}
                    {!invoice.bank?.account_number && !invoice.bank?.sort_code && (
                      <p className="text-[11px] text-slate-500 mt-2">Bank details are on the invoice PDF you were sent.</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {!invoice?.hide_platform_branding && <p className="text-center text-[10px] text-slate-500 mt-4" data-testid="pay-powered-by">Powered by Fixlr</p>}
      </div>
    </div>
  );
}
